import dayjs from 'dayjs'
import { AccountSelector, InputWrapper, Select, TextTooltip } from '@root/components'
import { FieldHelper, RemoveButton } from '../AccountingRuleForm.styled'
import { getVatRateLabelOptions } from '@root/constants'
import { isVATObliged, useBusinessContext } from '@root/context'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import { fetchAccount, IAccountingRule, IAccountingRuleBlueprintEntry } from '@root/query'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Control, Controller, FieldArrayPath, get, Path, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import styled, { keyframes } from 'styled-components'
import { trapSpacesForRequiredFields } from '@root/utils'

const ALLOWED_CHARS_REGEX = /[^0-9,.s*+/() -]/g

type Segment = { type: 'text' | 'tag'; content: string } | { type: 'cursor'; content: '' }

function parseSegments(value: string, isFocused: boolean, cursorPosition: number): Segment[] {
  if (!value && !isFocused) return []

  const parts: Segment[] = []
  let lastIndex = 0
  let cursorInserted = false

  const insertCursor = (atIndex: number) => {
    if (isFocused && !cursorInserted && cursorPosition === atIndex) {
      parts.push({ type: 'cursor', content: '' })
      cursorInserted = true
    }
  }

  insertCursor(0)

  for (let i = 0; i < value.length; i++) {
    if (value[i] === 's') {
      if (i > lastIndex) {
        const textContent = value.slice(lastIndex, i)
        if (isFocused && !cursorInserted && cursorPosition > lastIndex && cursorPosition <= i) {
          const cursorOffset = cursorPosition - lastIndex
          parts.push({ type: 'text', content: textContent.slice(0, cursorOffset) })
          parts.push({ type: 'cursor', content: '' })
          cursorInserted = true
          if (cursorOffset < textContent.length) {
            parts.push({ type: 'text', content: textContent.slice(cursorOffset) })
          }
        } else {
          parts.push({ type: 'text', content: textContent })
        }
      }
      insertCursor(i)
      parts.push({ type: 'tag', content: 's' })
      lastIndex = i + 1
      insertCursor(lastIndex)
    }
  }

  if (lastIndex < value.length) {
    const textContent = value.slice(lastIndex)
    const cursorOffset = cursorPosition - lastIndex
    if (
      isFocused &&
      !cursorInserted &&
      cursorPosition >= lastIndex &&
      cursorPosition <= value.length
    ) {
      if (cursorOffset > 0) {
        parts.push({ type: 'text', content: textContent.slice(0, cursorOffset) })
      }
      parts.push({ type: 'cursor', content: '' })
      cursorInserted = true
      if (cursorOffset < textContent.length) {
        parts.push({ type: 'text', content: textContent.slice(cursorOffset) })
      }
    } else {
      parts.push({ type: 'text', content: textContent })
    }
  }

  if (isFocused && !cursorInserted) {
    parts.push({ type: 'cursor', content: '' })
  }

  return parts
}

interface AmountFormulaInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  error?: { message?: string }
  label?: string
  required?: boolean
  replaceCommaWithDot: (value: string) => string
}

const AmountFormulaInput: React.FC<AmountFormulaInputProps> = ({
  id,
  value,
  onChange,
  onBlur,
  error,
  label,
  required = false,
  replaceCommaWithDot
}) => {
  const [t] = useTranslation()
  const [isFocused, setFocused] = useState(false)
  const [cursorPosition, setCursorPosition] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const errorMsg = error?.message

  const segments = useMemo(
    () => parseSegments(value, isFocused, cursorPosition),
    [value, isFocused, cursorPosition]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = e.target.value.replace(ALLOWED_CHARS_REGEX, '')
      onChange(sanitized)
      setCursorPosition(e.target.selectionStart ?? sanitized.length)
    },
    [onChange]
  )

  const handleSelect = useCallback(() => {
    const start = inputRef.current?.selectionStart ?? 0
    setCursorPosition(start)
  }, [])

  const handleFocus = useCallback(() => {
    setFocused(true)
    setCursorPosition(inputRef.current?.selectionStart ?? value.length)
  }, [value.length])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onChange(replaceCommaWithDot(e.target.value))
      setFocused(false)
      onBlur()
    },
    [onChange, replaceCommaWithDot, onBlur]
  )

  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const hasValue = value.length > 0

  return (
    <AmountField>
      <InputWrapper
        inputId={id}
        label={label}
        isFocused={isFocused}
        isInvalid={Boolean(errorMsg)}
        errorMsg={errorMsg}
        required={required}
      >
        <FormulaInputContainer onClick={focusInput}>
          <FormulaVisible aria-hidden>
            {hasValue || isFocused
              ? segments.map((segment, index) => {
                  if (segment.type === 'cursor') {
                    return <FormulaCaret key={`cursor-${index}`} />
                  }
                  if (segment.type === 'tag') {
                    return (
                      <TextTooltip
                        key={`tag-${index}`}
                        tooltip={t(
                          'settings.automation.accountingRules.form.document.amountVariableTooltip'
                        )}
                      >
                        <VariableChip>
                          {t(
                            'settings.automation.accountingRules.form.document.amountVariableLabel'
                          )}
                        </VariableChip>
                      </TextTooltip>
                    )
                  }
                  return <FormulaText key={`text-${index}`}>{segment.content}</FormulaText>
                })
              : null}
          </FormulaVisible>
          <HiddenInput
            ref={inputRef}
            id={id}
            type="text"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onSelect={handleSelect}
            onKeyUp={handleSelect}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
            autoComplete="off"
          />
        </FormulaInputContainer>
      </InputWrapper>
      {isFocused && (
        <FieldHelper>
          {t('settings.automation.accountingRules.form.document.amountHelper', {
            sum: t('settings.automation.accountingRules.form.document.amountVariableLabel')
          })}
        </FieldHelper>
      )}
    </AmountField>
  )
}

interface Props {
  baseName: FieldArrayPath<IAccountingRule>
  index: number
  control: Control<IAccountingRule>
  canRemove: boolean
  onRemove: () => void
}

const buildEntryFieldName = (
  baseName: FieldArrayPath<IAccountingRule>,
  index: number,
  key: keyof IAccountingRuleBlueprintEntry
): Path<IAccountingRule> => `${baseName}.${index}.${key}` as Path<IAccountingRule>

const replaceCommaWithDot = (value: string) => {
  return value.replaceAll(',', '.')
}

const Entry: React.FC<Props> = ({ baseName, index, control, canRemove, onRemove }) => {
  const [t] = useTranslation()
  const { data: vatCodeOptions } = useVATCodes()
  const { slug, data: business } = useBusinessContext()
  const { country_config: countryConfig } = business
  const vat_obliged = isVATObliged(business)
  const queryClient = useQueryClient()
  const vatRequestId = useRef(0)

  const {
    register,
    setValue,
    formState: { errors }
  } = useFormContext<IAccountingRule>()

  const applyAccountVat = (accountId: number | null) => {
    if (!vat_obliged) return

    const requestId = ++vatRequestId.current
    const vatCodeName = buildEntryFieldName(baseName, index, 'default_vat_code')
    const vatRateName = buildEntryFieldName(baseName, index, 'default_vat_rate_label')
    const fieldOptions = { shouldDirty: true, shouldTouch: true }

    if (!accountId) {
      setValue(vatCodeName, '' as never, fieldOptions)
      setValue(vatRateName, '', fieldOptions)
      return
    }

    queryClient
      .fetchQuery({
        queryKey: [slug, 'accounts', accountId],
        queryFn: () => fetchAccount({ slug, accountId })
      })
      .then(account => {
        if (requestId !== vatRequestId.current) return
        setValue(vatCodeName, account.default_vat_code ?? null, fieldOptions)
        setValue(vatRateName, account.default_vat_rate_label ?? '', fieldOptions)
      })
  }

  return (
    <StyledContainer>
      <StyledLine>
        <Controller
          control={control}
          name={buildEntryFieldName(baseName, index, 'account_id')}
          rules={{ required: { value: true, message: t('validation.required') } }}
          render={({ field, fieldState: { error } }) => (
            <AccountSelector
              {...field}
              ref={null}
              required={true}
              errors={error}
              label={t('settings.automation.accountingRules.form.document.accountLabel')}
              onChange={value => {
                field.onChange(value)
                applyAccountVat(value)
              }}
            />
          )}
        />
        <Controller
          control={control}
          name={buildEntryFieldName(baseName, index, 'amount')}
          rules={{
            required: { value: true, message: t('validation.required') },
            validate: value => trapSpacesForRequiredFields(value, t('validation.required'))
          }}
          render={({ field }) => (
            <AmountFormulaInput
              id={`${baseName}-${index}-amount`}
              value={String(field.value ?? '')}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={get(errors, `${baseName}.${index}.amount`)}
              label={t('settings.automation.accountingRules.form.document.amountLabel')}
              required
              replaceCommaWithDot={replaceCommaWithDot}
            />
          )}
        />
        <RemoveButton
          type="button"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label={t('general.delete')}
        >
          <FaTimes />
        </RemoveButton>
      </StyledLine>

      {vat_obliged && (
        <StyledLine>
          <Select
            id={`${baseName}.${index}.default_vat_code`}
            label={t('settings.automation.accountingRules.form.document.vatCodeLabel')}
            {...register(buildEntryFieldName(baseName, index, 'default_vat_code'), {
              required: { value: true, message: t('validation.required') }
            })}
            errors={get(errors, `${baseName}.${index}.default_vat_code`)}
            required={true}
          >
            <option hidden={true} disabled={true} selected={true} value={''} />
            {vatCodeOptions?.map(({ label, value, info }) => (
              <option key={value} value={value} title={info}>
                {t(label)}
              </option>
            ))}
          </Select>
          <Select
            label={t('settings.automation.accountingRules.form.document.vatRateLabel')}
            {...register(buildEntryFieldName(baseName, index, 'default_vat_rate_label'), {
              required: { value: true, message: t('validation.required') }
            })}
            errors={get(errors, `${baseName}.${index}.default_vat_rate_label`)}
            required={true}
          >
            <option hidden={true} disabled={true} selected={true} value={''} />

            {getVatRateLabelOptions({ countryConfig, dateAt: dayjs(), t, uniqueLabels: true }).map(
              ({ label, value }, optionIndex) => (
                <option key={`option-${optionIndex}`} value={value}>
                  {label}
                </option>
              )
            )}
          </Select>
          <RemoveSpacer />
        </StyledLine>
      )}
    </StyledContainer>
  )
}

export default Entry

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;

  label {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    font-weight: 700;
  }

  .input-wrapper {
    margin-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    padding-right: 2rem;
  }
`

const StyledLine = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 250px) 28px;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    grid-template-columns: minmax(0, 1fr);

    > ${RemoveButton} {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1;
    }
  }
`

const RemoveSpacer = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    display: none;
  }
`

const AmountField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const caretBlink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`

const VariableChip = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => `${theme.colors.primary}26`};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  padding: 0.15rem 0.4rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  margin: 0 0.1em;
  font-family: inherit;
`

const FormulaInputContainer = styled.div`
  position: relative;
  flex-grow: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 1.5em;
  cursor: text;
`

const FormulaVisible = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-grow: 1;
  min-width: 0;
  font-size: inherit;
  white-space: pre;
  gap: 0;
`

const FormulaText = styled.span`
  font-size: inherit;
`

const FormulaCaret = styled.span`
  display: inline-block;
  width: 2px;
  min-width: 2px;
  height: 1.1em;
  background: ${({ theme }) => theme.colors.neutralBlack};
  animation: ${caretBlink} 1s step-end infinite;
  vertical-align: text-bottom;
`

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  outline: none;
  font-size: inherit;
  font-family: inherit;
`
